// BottomNav — app-style mobile bottom tab bar, mounted globally in Layout so it
// appears on every staff page. Shows Home + the user's pinned boards + More.
// "More" opens a full-screen sheet with every board and a Customize mode to
// choose the bar's boards (max 4), reorder, and hide. Saved per user.
//
// Mobile only (sm:hidden) — desktop keeps the top header nav. Hidden on the
// full-screen Canvas tools so it doesn't cover the map controls. Jun 17, 2026.

import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  type HomeLayout, MAX_PINS, LANDING_BY_ROLE,
  filterDests, isCompanyViewRole, defaultPinsFor, requiredPinsFor, loadLayout, persistLayout, computePinned, computeRest,
} from "./homeNav";

// Routes where the bar is suppressed (full-screen tools with their own chrome).
const HIDE_ON = new Set(["/canvas", "/canvas/appt"]);

export default function BottomNav() {
  const { member, role, isSuperUser } = useAuth();
  const location = useLocation();
  const [layout, setLayout] = useState<HomeLayout>({ pinned: [], order: [], hidden: [] });
  const [moreOpen, setMoreOpen] = useState(false);
  const [editLayout, setEditLayout] = useState(false);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [deskMore, setDeskMore] = useState(false);

  useEffect(() => { if (member) setLayout(loadLayout(member.id)); }, [member?.id]);
  // close menus when navigating
  useEffect(() => { setMoreOpen(false); setDeskMore(false); }, [location.pathname]);

  if (!member) return null;
  if (HIDE_ON.has(location.pathname)) return null;

  const isCompanyView = isCompanyViewRole(role, !!isSuperUser);
  const dests = filterDests(role, !!isSuperUser, member.secondary_role, member.email);
  // Dual-role staff get their secondary board added to the default pins (e.g.
  // Sam: RS default + Drone). Their saved picks still win once they customize.
  let defaultTos = defaultPinsFor(role, isCompanyView);
  const secBoard = member.secondary_role ? LANDING_BY_ROLE[member.secondary_role] : null;
  if (secBoard && !defaultTos.includes(secBoard)) {
    defaultTos = Array.from(new Set([defaultTos[0], secBoard, ...defaultTos.slice(1)]));
  }
  const pinnedDests = computePinned(layout, dests, defaultTos, requiredPinsFor(role));
  const pinnedTos = pinnedDests.map((d) => d.to);
  const pinFull = pinnedTos.length >= MAX_PINS;
  const visibleRest = computeRest(layout, dests, pinnedTos, editLayout);

  const save = (next: Partial<HomeLayout>) => {
    const l = { ...layout, ...next };
    setLayout(l); persistLayout(member.id, l);
  };
  const pin = (key: string) => { if (pinFull || pinnedTos.includes(key)) return; save({ pinned: [...pinnedTos, key], hidden: layout.hidden.filter((h) => h !== key) }); };
  const unpin = (key: string) => save({ pinned: pinnedTos.filter((k) => k !== key) });
  const movePin = (key: string, dir: -1 | 1) => { const a = [...pinnedTos]; const i = a.indexOf(key), j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; save({ pinned: a }); };
  const moveDest = (key: string, dir: -1 | 1) => { const keys = computeRest(layout, dests, pinnedTos, true).map((d) => d.to); const i = keys.indexOf(key), j = i + dir; if (j < 0 || j >= keys.length) return; [keys[i], keys[j]] = [keys[j], keys[i]]; save({ order: keys }); };
  const toggleHide = (key: string) => save({ order: computeRest(layout, dests, pinnedTos, true).map((d) => d.to), hidden: layout.hidden.includes(key) ? layout.hidden.filter((h) => h !== key) : [...layout.hidden, key] });

  const active = (to: string) => {
    // A destination with a query (Daily Plans = ?mode=today) is active only on that exact view.
    if (to.includes("?")) return location.pathname + location.search === to;
    return location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/"));
  };
  const isHome = location.pathname === "/" || location.pathname === "/staff";
  const deskTab = (on: boolean) => "shrink-0 px-3 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap " + (on ? "text-bardin-orange border-bardin-orange" : "text-slate-600 border-transparent hover:text-slate-900");

  return (
    <>
      {/* DESKTOP — top tab row under the header */}
      <div className="hidden sm:block bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1 flex-wrap">
          <Link to="/" className={deskTab(isHome)}><span className="mr-1">🏠</span>Home</Link>
          {pinnedDests.map((d) => (
            <Link key={d.to} to={d.to} {...(d.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})} className={deskTab(active(d.to))}>
              <span className="mr-1">{d.icon}</span>{d.short}
            </Link>
          ))}
          <div className="relative">
            <button onClick={() => setDeskMore((v) => !v)} className={deskTab(deskMore)}>⋯ More</button>
            {deskMore && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setDeskMore(false)} />
                <div className="absolute left-0 top-full mt-1 z-40 w-64 max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl py-1.5">
                  {visibleRest.map((d) => {
                    const c = "flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50";
                    const inner = (<><span>{d.icon}</span><span className="truncate">{d.label}</span></>);
                    return d.ext
                      ? <a key={d.to} href={d.to} target="_blank" rel="noopener noreferrer" onClick={() => setDeskMore(false)} className={c}>{inner}</a>
                      : <Link key={d.to} to={d.to} onClick={() => setDeskMore(false)} className={c}>{inner}</Link>;
                  })}
                  <div className="border-t border-slate-100 mt-1 pt-1">
                    <button onClick={() => { setDeskMore(false); setMoreOpen(true); }} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm font-semibold text-bardin-teal hover:bg-slate-50">✏️ Customize…</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE — fixed bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 flex" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <Link to="/" className={"flex-1 py-2 text-center text-[10px] font-bold truncate px-0.5 " + (isHome ? "text-bardin-orange" : "text-slate-500")}>
          <span className="block text-lg leading-none mb-0.5">🏠</span>Home
        </Link>
        {pinnedDests.map((d) => (
          <Link key={d.to} to={d.to} {...(d.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            className={"flex-1 py-2 text-center text-[10px] font-bold truncate px-0.5 " + (active(d.to) ? "text-bardin-orange" : "text-slate-500")}>
            <span className="block text-lg leading-none mb-0.5">{d.icon}</span>{d.short}
          </Link>
        ))}
        <button onClick={() => setMoreOpen(true)}
          className={"flex-1 py-2 text-center text-[10px] font-bold " + (moreOpen ? "text-bardin-orange" : "text-slate-500")}>
          <span className="block text-lg leading-none mb-0.5">⋯</span>More
        </button>
      </div>

      {moreOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white sm:flex-row sm:items-center sm:justify-center sm:bg-black/40 sm:p-6"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
          onClick={(e) => { if (e.target === e.currentTarget) { setEditLayout(false); setMoreOpen(false); } }}>
          <div className="flex flex-col flex-1 min-h-0 w-full bg-white sm:flex-none sm:max-w-2xl sm:max-h-[85vh] sm:rounded-2xl sm:overflow-hidden sm:shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <span className="text-base font-bold text-bardin-ink">All boards</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditLayout((v) => !v)}
                className={"text-[12px] font-bold px-3 py-1.5 rounded-full " + (editLayout ? "bg-bardin-ink text-white" : "bg-slate-100 text-slate-600")}>
                {editLayout ? "✓ Done" : "✏️ Customize"}
              </button>
              <button onClick={() => { setEditLayout(false); setMoreOpen(false); }}
                className="text-[13px] font-bold px-3 py-1.5 rounded-full bg-slate-900 text-white">Close</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {editLayout && (
              <div className="text-[11px] text-slate-400 mb-3">📌 puts a board in the bottom bar (max {MAX_PINS}) · drag or ↑ ↓ to sort · 👁 hides it. Saved to your account.</div>
            )}
            {editLayout && pinnedDests.length > 0 && (
              <>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">In your bottom bar</div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {pinnedDests.map((d) => (
                    <div key={d.to} className="flex items-center gap-1.5 rounded-xl border border-orange-300 bg-orange-50 px-2 py-2 text-[13px] font-semibold">
                      <span className="text-base shrink-0">{d.icon}</span>
                      <span className="truncate flex-1 min-w-0">{d.label}</span>
                      {requiredPinsFor(role).includes(d.to) ? (
                        <span className="px-0.5 text-[10px] font-bold text-slate-400" title="Always in your bar">fixed</span>
                      ) : (
                        <>
                          <button onClick={() => movePin(d.to, -1)} className="px-0.5 text-slate-400 text-base leading-none">↑</button>
                          <button onClick={() => movePin(d.to, 1)} className="px-0.5 text-slate-400 text-base leading-none">↓</button>
                          <button onClick={() => unpin(d.to)} className="px-0.5 text-base leading-none">✕</button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{editLayout ? "Everything else" : "Go to"}</div>
            <div className="grid grid-cols-2 gap-2 pb-6">
              {visibleRest.map((d) => {
                if (editLayout) {
                  const isHidden = layout.hidden.includes(d.to);
                  return (
                    <div key={d.to} draggable
                      onDragStart={() => setDragKey(d.to)} onDragEnd={() => setDragKey(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (dragKey) { const keys = computeRest(layout, dests, pinnedTos, true).map((x) => x.to); const f = keys.indexOf(dragKey), t = keys.indexOf(d.to); if (f >= 0 && t >= 0) { keys.splice(t, 0, keys.splice(f, 1)[0]); save({ order: keys }); } } setDragKey(null); }}
                      className={"flex items-center gap-1.5 rounded-xl border bg-white px-2 py-2 text-[13px] font-semibold " +
                        (isHidden ? "opacity-40 border-dashed border-slate-300 " : "border-slate-200 ") +
                        (dragKey === d.to ? "ring-2 ring-bardin-orange " : "")}>
                      <span className="cursor-grab text-slate-300 select-none">⠿</span>
                      <span className="text-base shrink-0">{d.icon}</span>
                      <span className="truncate flex-1 min-w-0">{d.label}</span>
                      <button onClick={() => pin(d.to)} disabled={pinFull} title={pinFull ? "Bar full" : "Pin to bar"} className={"px-0.5 text-base leading-none " + (pinFull ? "opacity-25" : "")}>📌</button>
                      <button onClick={() => moveDest(d.to, -1)} className="px-0.5 text-slate-400 text-base leading-none">↑</button>
                      <button onClick={() => moveDest(d.to, 1)} className="px-0.5 text-slate-400 text-base leading-none">↓</button>
                      <button onClick={() => toggleHide(d.to)} className="px-0.5 text-base leading-none">{isHidden ? "🚫" : "👁"}</button>
                    </div>
                  );
                }
                const cls = "flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-semibold text-bardin-ink";
                const inner = (<><span className="text-lg shrink-0">{d.icon}</span><span className="truncate">{d.label}</span></>);
                return d.ext
                  ? <a key={d.to} href={d.to} target="_blank" rel="noopener noreferrer" onClick={() => setMoreOpen(false)} className={cls}>{inner}</a>
                  : <Link key={d.to} to={d.to} onClick={() => setMoreOpen(false)} className={cls}>{inner}</Link>;
              })}
            </div>
          </div>
          </div>
        </div>
      )}
    </>
  );
}
